<template>
    <div>
        <b-form>
            <b-alert
                v-for="(alertMessage, idx) in alertMessages"
                :class="idx < alertMessages.length - 1 ? 'mb-2' : ''"
                :key="idx"
                :show="showAlert"
                variant="warning"
            >
                <div>
                    <i class="cus-error"></i>
                    {{ alertMessage }}
                </div>
            </b-alert>

            <b-form-group
                label="Open Diagnoses:"
                label-cols="4"
                label-for="diagnoses"
                class="grooming"
            >
                <v-select
                    id="diagnoses"
                    ref="vselectDiagnoses"
                    v-model="data.diagnosis"
                    :options="diagnosisFilter"
                    :label="'text'"
                    name="diagnoses"
                    multiple
                    aria-placeholder="true"
                    placeholder="Open Diagnoses (optional)"
                    :disabled="data.isMultiplePatient"
                ></v-select>
            </b-form-group>

            <b-form-group
                :label-for="`given_date`"
                :label-cols="4"
                label="Given:"
            >
                <vet-datetime
                    id="given_date"
                    v-model="data.given_date"
                    v-validate="'required'"
                    :placeholder="{
                        date: 'Date (required)',
                        time: 'Time (required)'
                    }"
                    container="#createUpdateRecord"
                    rules="required"
                    name="given_date"
                    format="moment"
                ></vet-datetime>
            </b-form-group>

            <b-form-group
                :label-for="`next_due`"
                label="Next Due:"
                label-cols="4"
            >
                <date-picker
                    ref="datePickerNextDue"
                    @dp-show="showDatePicker"
                    :config="datePickerOptions"
                    v-model="data.next_due"
                    placeholder="Reminder Date (optional)"
                    class="form-control"
                    name="next_due"
                ></date-picker>
            </b-form-group>

            <b-form-group
                :label-for="`record_note`"
                :label-cols="4"
                label="Notes:"
            >
                <editable
                    id="record_note"
                    v-model="data.record_note"
                    placeholder="Notes (optional)"
                ></editable>
            </b-form-group>

            <b-form-group
                class="prescription grooming"
                :label-for="`filled_by`"
                :label-cols="4"
                label="Filled By:"
            >
                <b-form-select
                    id="filled_by"
                    v-model="data.filled_by"
                    v-validate="'required'"
                    :options="filledBy"
                    class="col mr-auto form-control"
                    name="filled_by"
                ></b-form-select>
            </b-form-group>

            <b-form-group label="Lot:" label-cols="4" label-for="lot">
                <v-select
                    id="lot"
                    v-model="data.lot"
                    :options="lotOptionFilter"
                    :label="'text'"
                    placeholder="Select lot (optional)"
                    name="lot"
                    multiple
                    :disabled="isCubexItem(data.item) || isCubex"
                    class="show-actions"
                    :loading="loadingLot"
                    v-b-tooltip.hover
                    :title="
                        isCubexItem(data.item) || isCubex
                            ? 'This item is stored and managed in Cubex. The corresponding lot number will be updated in DaySmart once the item is dispensed from the cabinet and it is determined.'
                            : ''
                    "
                >
                    <template #list-header>
                        <div class="menu">
                            <a
                                @click.stop="addLot"
                                class="badge badge-success rounded-0"
                                >Add New</a
                            >
                        </div>
                    </template>
                </v-select>
            </b-form-group>

            <b-form-group
                :label-for="`tag`"
                :label-cols="4"
                label="Tag No:"
                class="vaccination grooming"
            >
                <div class="form-inline">
                    <b-form-input
                        id="tag"
                        v-model="data.tag"
                        placeholder="Tag Number (optional)"
                        name="tag"
                        class="col mr-auto"
                        :disabled="data.isMultiplePatient"
                    ></b-form-input>
                </div>
            </b-form-group>

            <b-form-group
                class="prescription vaccination grooming"
                :label-for="`route`"
                :label-cols="4"
                label="Route:"
            >
                <b-form-select
                    id="route"
                    v-model="data.route"
                    :options="routesFilter"
                    class="col mr-auto form-control"
                    name="route"
                    :disabled="data.isMultiplePatient"
                ></b-form-select>
            </b-form-group>

            <b-form-group
                class="prescription vaccination grooming"
                :label-for="`location`"
                :label-cols="4"
                label="Location:"
            >
                <b-form-select
                    id="location"
                    v-model="data.location"
                    :options="locationsFilter"
                    class="col mr-auto form-control"
                    name="location"
                    :disabled="data.isMultiplePatient"
                ></b-form-select>
            </b-form-group>

            <b-form-group
                class="prescription preventative diet other_medical grooming"
                :label-for="`refill`"
                :label-cols="4"
                label="Refill:"
            >
                <div class="form-inline">
                    <b-form-input
                        id="refill"
                        v-model="data.refill"
                        v-validate="'numeric'"
                        :class="{ 'border-danger': errors.has('refill') }"
                        name="refill"
                        class="col-2"
                        placeholder="Number of Refills Allowed (Optional)"
                    ></b-form-input>
                    <span class="ml-2 mr-2">time(s) until</span>
                    <date-picker
                        @dp-show="showDatePicker"
                        :config="datePickerOptions"
                        v-model="data.expire"
                        :placeholder="
                            'Allow Refills Until (' +
                            (data.refill > 0 ? 'required' : 'optional') +
                            ')'
                        "
                        name="expire"
                        style="width: 52%;"
                        v-validate="{
                            required: data.refill > 0
                        }"
                        :class="{
                            'border-danger': errors.has('expire')
                        }"
                    ></date-picker>
                </div>
            </b-form-group>
            <b-form-group
                :label-for="`instruction`"
                :label-cols="4"
                label="Instructions:"
                class="prescription diet other_medical service vaccination grooming"
            >
                <editable
                    id="instruction"
                    ref="instruction"
                    v-model="data.instruction"
                    placeholder="Instructions For Label (optional)"
                    className="h-unset"
                    :defaultHeight="81"
                    :class="{
                        'border-danger': instructionCount < 0
                    }"
                ></editable>
                <span
                    v-bind:style="[
                        instructionCount >= 0
                            ? { color: 'grey' }
                            : { color: 'red' }
                    ]"
                    style="font-size: 10px; padding-left: 53%;"
                    >{{ instructionCount }} character(s) remaining</span
                >
            </b-form-group>

            <b-form-group
                v-if="data.filled_by == 1"
                class="prescription diet other_medical vaccination service grooming"
                :label-for="`printer`"
                :label-cols="4"
                label="Label:"
            >
                <div class="form-inline">
                    <b-form-select
                        id="printer"
                        v-model="data.printer"
                        :options="printerList"
                        :disabled="isCubexItem(data.item) || isCubex"
                        v-b-tooltip.hover
                        :title="
                            isCubexItem(data.item) || isCubex
                                ? 'This functionality is not currently available when the Cubex Integration is enabled'
                                : ''
                        "
                        class="col-8 form-control"
                        name="printer"
                    ></b-form-select>
                    <span class="col-1 p-0 text-center">X</span>
                    <b-form-input
                        id="print_quantity"
                        :disabled="
                            data.printer === 0 ||
                            data.printer === 'no' ||
                            data.printer === 'local'
                        "
                        v-model="data.print_quantity"
                        name="print_quantity"
                        class="col-3"
                        placeholder="Quantity"
                    ></b-form-input>
                </div>
            </b-form-group>
        </b-form>
        <NewLot ref="newNewLotWindow" @saved="onChangeLot"></NewLot>
    </div>
</template>
<script>
import { filter, merge } from "lodash";
import ITEMTYPES from "@/constants/item-types";
import vSelect from "vue-select";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import moment from "moment";
import NewLot from "@/module/commons/inventory/custom-lot-modal";
import RecordHelper from "@/mixins/record";
import CubexMixin from "@/mixins/cubex";
import Editable from "@/components/elements/editable-textarea";
const entities = require("entities");

export default {
    name: "createUpdateRecord",
    components: {
        vSelect,
        Editable,
        vetDatetime,
        NewLot
    },
    mixins: [RecordHelper, CubexMixin],
    props: {
        hideBackdrop: {
            type: Boolean,
            default: false,
            required: false
        },
        invoiceItem: {
            type: Object,
            default: null,
            required: false
        },
        startDate: {
            type: Object,
            required: true
        },
        isDuplicate: {
            type: Boolean,
            default: false
        },
        updateLot: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            ITEMTYPES: ITEMTYPES,
            diagnosisOption: [],
            routes: [],
            locations: [],
            instructionCount: 200,
            alertMessages: [],
            data: {
                // this param cannot be the same as in create-item as the param will be merged
                diagnosis: [],
                lot: [],
                route: 0,
                location: 0,
                given_date: null,
                next_due: null,
                record_note: null,
                refill: 0,
                item: null,
                expire: null,
                filled_by: 1,
                printer: "no",
                print_quantity: 1,
                tag: null,
                instruction: null
            },
            datePickerOptions: {
                format: "DD/MM/YYYY",
                widgetParent: "#createUpdateRecord"
            },
            itemLotOption: [],
            printerList: [],
            defaultPrinter: null,
            loading: [],
            itemNextDue: null,
            itemNextDuePeriod: "days",
            loadingLot: false
        };
    },
    computed: {
        isNew() {
            return this.invoiceItem == null;
        }
    },
    watch: {
        loading(value) {
            this.$emit("loading", !(value.length == 0));
        }
    },
    mounted() {
        this.getRoutes();
        this.getLocation();
        this.datePickerOptions.format = this.getDateFormat();
    },
    methods: {
        async open(params) {
            let self = this;
            self.isCubex = this.isCubexItem(params.item);
            self.loading.push("record-form");
            self.data.printer = "no";

            await self.reset();

            // MERGE THE INVOICE ITEM PAGE TO DATA
            self.data = merge(self.data, params);

            if (self.isCubex) {
                self.data.lot = [];
            }

            if (
                parseInt(self.data.item.item_type) !=
                ITEMTYPES.TYPES.TYPE_GROOMING
            ) {
                self.getDiagnosis(self.data.patientId);
            }

            if (
                parseInt(self.data.item.item_type) !=
                ITEMTYPES.TYPES.TYPE_GROOMING
            ) {
                await self.getPrinterList();
                self.setPrinterLabel(self.data.item.print_label);
            }

            self.data.given_date = moment.isMoment(self.startDate)
                ? self.startDate
                : moment.unix(self.startDate).tz(self._timeZone);
            this.data.next_due = null;
            this.itemNextDue = null;
            this.itemNextDuePeriod = "days";

            self.appendData();

            self.checkItemType(params.item_type);
            self.$nextTick(() => {
                self.removeLoadingItem("record-form");
                self.dataLoaded = true;

                if (
                    parseInt(self.data.item.item_type) ==
                    ITEMTYPES.TYPES.TYPE_GROOMING
                ) {
                    if (this.$refs.datePickerNextDue) {
                        // auto open will make the datepicker open from wrong position
                        // this.$refs.datePickerNextDue.$el.focus();
                    }
                } else {
                    // VET-1371 - remove the auto focus dropdown
                    // if (this.$refs.vselectDiagnoses) {
                    //     this.$refs.vselectDiagnoses.$el
                    //         .querySelector("input")
                    //         .focus();
                    // }
                }
            });

            self.$emit("scroll-top");
        },
        appendData() {
            if (
                this.isNew ||
                (this.invoiceItem && this.invoiceItem.record == null)
            ) {
                // FOR new invoiceitem or invoiceItem with no record
                this.itemNextDuePeriod = "days";

                if (
                    this.data.item.due &&
                    (this.data.item.due.label || this.data.item.due.name)
                ) {
                    let dueName =
                        this.data.item.due.name || this.data.item.due.label;
                    let due = dueName.split(" ");

                    if (due.length == 2) {
                        let dueDate = this.data.given_date
                            ? this.data.given_date.clone().add(due[0], due[1])
                            : moment().tz(this._timeZone).add(due[0], due[1]);

                        this.itemNextDue = due[0];
                        this.itemNextDuePeriod = due[1];
                        this.data.next_due = dueDate.format(
                            this.getDateFormat()
                        );
                    }
                }

                this.getItemInventoryLot(this.data.item.id, false);

                // can use vaccinationLocation and vaccinationRoute from API barramundi instead of from ci
                this.data.instruction = this.data.item.instruction;
                this.data.location = this.data.item.location;
                this.data.route = this.data.item.route;
            } else {
                // FOR edit & duplicate invoiceitem
                const RECORD = this.invoiceItem.record_resource;

                this.data.given_date = moment
                    .unix(RECORD.form_data.record_resource.RecordDate)
                    .tz(this._timeZone);

                this.itemNextDuePeriod = "days";
                if (RECORD.form_data.record_resource.ReminderDate) {
                    let due = moment
                        .unix(RECORD.form_data.record_resource.ReminderDate)
                        .tz(this._timeZone);
                    this.itemNextDue = due.diff(
                        this.data.given_date,
                        this.itemNextDuePeriod
                    );
                    this.data.next_due = due.format(
                        this.datePickerOptions.format
                    );
                }

                this.data.record_note =
                    RECORD.form_data.record_resource.RecordNote;
                this.data.filled_by = RECORD.form_data.record_resource.IsClinic;

                /*
                 ** FOR LOT FIELD,
                 ** If duplicate : follow duplicate medical record, select new available lot
                 ** If edit : the usual flow, just show what is selected during create new
                 */
                if (this.isDuplicate) {
                    this.getItemInventoryLot(this.invoiceItem.item_id, false);
                } else {
                    this.applyLot(RECORD, this.updateLot);
                    this.getItemInventoryLot(this.invoiceItem.item_id, true);
                }

                this.data.tag = RECORD.form_data.record_resource.Tag;
                this.data.route = RECORD.form_data.record_resource.RouteID
                    ? RECORD.form_data.record_resource.RouteID
                    : 0;
                this.data.location = RECORD.form_data.record_resource.LocationID
                    ? RECORD.form_data.record_resource.LocationID
                    : 0;

                this.data.refill = RECORD.form_data.record_resource.Refill;
                this.data.expire = RECORD.form_data.record_resource.expire;
                this.data.instruction =
                    RECORD.form_data.record_resource.Instruction;

                this.data.item = {
                    id: RECORD.form_data.item_resource.ID,
                    label: RECORD.form_data.item_resource.DisplayName
                };
            }
        },
        async getItemInventoryLot(itemId, fetchOnly = false) {
            this.loading.push("get-inventory-lot");
            await this.getInventoryLot(itemId, fetchOnly);
            this.removeLoadingItem("get-inventory-lot");
        },
        appendEditDiagnoses() {
            if (
                !this.isNew &&
                this.invoiceItem &&
                this.invoiceItem.record_resource &&
                this.invoiceItem.record_resource.form_data.diagnoses_ids
            ) {
                this.data.diagnosis = filter(this.diagnosisOption, option => {
                    return this.invoiceItem.record_resource.form_data.diagnoses_ids.includes(
                        option.value
                    );
                });
            }
        },
        async validate() {
            // Fixed Issue VET-2204: When adding this.$validator.validate() directly to the return, it will return true, didnt find the reason why, might due to internal library issue
            // a solution was to assign it to a variable before return
            let valid = await this.$validator.validate();
            return valid && this.instructionCount >= 0;
        },
        checkItemType(itemType) {
            $(
                ".prescription, .other_medical, .vaccination, .diet, .grooming, .service"
            ).show();

            switch (parseInt(itemType)) {
                case ITEMTYPES.TYPES.TYPE_DIET:
                    $(".prescription, .other_medical, .vaccination").hide();
                    $(".diet").show();
                    break;
                case ITEMTYPES.TYPES.TYPE_MEDICATION:
                case ITEMTYPES.TYPES.TYPE_ANESTHESIA:
                    $(".other_medical, .vaccination, .diet").hide();
                    $(".prescription").show();
                    break;
                case ITEMTYPES.TYPES.TYPE_VACCINE:
                    $(".diet, .prescription, .other_medical").hide();
                    $(".vaccination").show();
                    break;
                case ITEMTYPES.TYPES.TYPE_OTHER_MEDICAL:
                case ITEMTYPES.TYPES.TYPE_NON_MEDICAL:
                    $(".vaccination, .diet, .prescription").hide();
                    $(".other_medical").show();
                    break;
                case ITEMTYPES.TYPES.TYPE_GROOMING:
                    $(".grooming").hide();
                    break;
                default:
                    // ---  service
                    $(
                        ".vaccination, .diet, .prescription, .other_medical"
                    ).hide();
                    $(".service").show();
                    break;
            }
        },
        decodedHe: function (string) {
            return entities.decodeHTML(string);
        },
        removeLoadingItem(item) {
            // when rendering, will add in this loading
            // when done rendered, remove from the loading array
            const index = this.loading.indexOf(item);
            if (index > -1) {
                this.loading.splice(index, 1);
            }
        },
        reset() {
            this.$validator.reset();
            this.diagnosisOption = [];
            this.itemLotOption = [];
            this.instructionCount = 200;
            this.alertMessages = [];
            this.data.diagnosis = [];
            this.data.lot = [];
            this.data.route = 0;
            this.data.location = 0;
            this.data.next_due = null;
            this.data.record_note = null;
            this.data.refill = 0;
            this.data.expire = null;
            this.data.filled_by = 1;
            this.data.printer = "no";
            this.data.print_quantity = 1;
            this.data.tag = null;
            // givenDate will not reset
        }
    }
};
</script>
